import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import getUserGeo from '@sdk/utils/api/getUserGeo';
import { SaveClientFlags } from '@sdk/authentication/types';

import getUserRoleName from '@web/common/utils/getUserRoleName';
import { shouldDisplaySatisfactionSurvey } from '@web/components/survey/utils';
import { SurveyType } from '@web/components/survey/types';

// spreadsheet url: https://docs.google.com/spreadsheets/d/1Wtj1FbT_jAiwe4TWKbk5MJ8JVhIhdnnK5zledDyu29k
const NPS_FORM_URL =
  'https://script.google.com/macros/s/AKfycbyvv9wFQYWwhbKJP-j1IDzo3h75NAj9xZUQDCjTGgjKShLIlGkfuHHB_DHAe-Y1vwZzAQ/exec';
const POSITIVE_RATING = 9;
const BAD_RATING = 6;
const NPS_FLAG_KEY = 'npsRateDate';

type NPSFormData = {
  comment_1: string;
  country_name: string;
  email: string;
  industry: string;
  influence_comment: string;
  location_count: string;
  nps_score: string;
  toast_integration_status: string;
  org_creation: string;
  org_id: string;
  org_name: string;
  rating_influence: string;
  role: string;
  subscription: string;
  user_count: string;
  user_id: string;
  user_name: string;
};

type NpsFeedback = {
  npsScore: number | null;
  scoreComment: string;
  influence: string;
  influenceComment: string;
};

export function isScorePositive(score: number): boolean {
  return score >= POSITIVE_RATING;
}

export function isBadScore(score: number): boolean {
  return score <= BAD_RATING;
}

type AdditionalFeedbackData = {
  user: AuthUserType;
  subscriptionName: string;
  usersCount: number;
  locationsCount: number;
  saveClientFlags: SaveClientFlags;
};

export async function sendNpsFeedback(
  feedback: NpsFeedback,
  additionalData: AdditionalFeedbackData,
  toastIntegrationStatus: string
): Promise<void> {
  const isValid = validateNpsFeedback(feedback);

  if (isValid && !__DEV__) {
    const geo = await getUserGeo();
    const data = getNpsFormData(feedback, additionalData, geo.country, toastIntegrationStatus);

    fetch(`${NPS_FORM_URL}?${new URLSearchParams(data)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  updateNpsRateDate(additionalData.saveClientFlags);
}

function getNpsFormData(
  feedback: NpsFeedback,
  additionalData: AdditionalFeedbackData,
  country: string,
  toastIntegrationStatus: string
): NPSFormData {
  const { user, subscriptionName, usersCount, locationsCount } = additionalData;
  const dismissedLabel = '**dismissed**';

  return {
    user_id: String(user.id),
    org_id: String(user.org.id),
    user_name: user.fullname,
    email: user.email,
    org_name: user.org.name,
    industry: user.org.businessCategory,
    subscription: subscriptionName,
    country_name: country,
    org_creation: String(user.org.createdAt).substr(0, 10),
    user_count: String(usersCount),
    role: getUserRoleName(user.role),
    nps_score: typeof feedback.npsScore === 'number' ? String(feedback.npsScore) : dismissedLabel,
    comment_1: feedback.scoreComment || dismissedLabel,
    toast_integration_status: toastIntegrationStatus || undefined,
    location_count: String(locationsCount),
    rating_influence: feedback.influence || dismissedLabel,
    influence_comment: feedback.influenceComment,
  };
}

function validateNpsFeedback(feedback: NpsFeedback) {
  return typeof feedback.npsScore === 'number';
}

export function shouldDisplayNpsScore(user: AuthUserType): boolean {
  return shouldDisplaySatisfactionSurvey(user, SurveyType.nps);
}

export function updateNpsRateDate(saveClientFlags: SaveClientFlags): void {
  saveClientFlags({ flags: { [NPS_FLAG_KEY]: new Date().toISOString() } });
}
